import React, {
  createContext,
  useCallback,
  useContext,
  useEffect
} from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';

const SS_API_KEY = process.env.REACT_APP_SS_SEGMENT_API_KEY;
const RR_API_KEY = process.env.REACT_APP_RR_SEGMENT_API_KEY;

const analytics = new AnalyticsBrowser();

/**
 * @type {import('react').Context<{
 *   apiKey: string | null;
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 * }>}
 */
export const SegmentContext = createContext();

export function SegmentProvider({ children = null }) {
  const { userData, storeProperties } = useContext(AuthContext);

  const storeName = _get(storeProperties, 'store_name', '');
  const ssSellerId = _get(storeProperties, 'ss_seller_id', '');
  const storeUniqueName = _get(storeProperties, 'store_unique_name', '');
  const isSSR = _get(storeProperties, 'is_ssr_retailer', false);

  const apiKey = isSSR ? SS_API_KEY : RR_API_KEY;
  const brand = isSSR ? 'ShipStation Returns' : 'Return Rabbit';
  const merchantId = !!storeUniqueName
    ? `${isSSR ? 'SSR' : 'RR'}-${storeUniqueName}`
    : null;

  useEffect(() => {
    const userId = _get(userData, 'userId');
    const email = _get(userData, 'email');
    const firstName = _get(userData, 'firstName');
    const lastName = _get(userData, 'lastName');

    if (apiKey && userId && merchantId && storeName) {
      try {
        analytics.load({ writeKey: apiKey });
        analytics.identify(userId, {
          group_id: merchantId,
          cx_merchant_id: merchantId,
          customer_id: userId,
          user_id: userId,
          brand,
          email,
          first_name: firstName,
          last_name: lastName,
          company_name: storeName,
          seller_id: ssSellerId,
          user_role: 'SHOPPER'
        });
      } catch (error) {
        console.error('Failed to identify user in Segment:', error);
      }

      return () => {
        try {
          analytics.reset();
        } catch (error) {
          console.error('Failed to reset Segment:', error);
        }
      };
    }
  }, [apiKey, brand, userData, merchantId, storeName, ssSellerId]);

  const track = useCallback(
    (eventName, eventProperties = {}) => {
      const userId = _get(userData, 'userId', '');
      const email = _get(userData, 'email', '');

      if (!apiKey || !userId || !merchantId) return;

      try {
        analytics.track(eventName, {
          brand,
          cx_merchant_id: merchantId,
          user_id: userId,
          email,
          ...eventProperties
        });
      } catch (error) {
        console.error('Failed to track event in Segment:', error);
      }
    },
    [apiKey, brand, userData, merchantId]
  );

  return (
    <SegmentContext.Provider value={{ apiKey, track }}>
      {children}
    </SegmentContext.Provider>
  );
}
